<script setup lang="ts">
import { computed, useSlots } from 'vue'

import { Loader2, type LucideIcon } from 'lucide-vue-next'
import { VariantProps, tv } from 'tailwind-variants'

const button = tv({
  slots: {
    base: [
      'relative inline-flex items-center justify-center gap-2 font-medium whitespace-nowrap',
      'border rounded-none transition-colors select-none',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--btn) focus-visible:ring-offset-2 focus-visible:ring-offset-surface',
      'disabled:pointer-events-none disabled:opacity-50',
      'aria-busy:cursor-progress',
    ],
    icon: 'shrink-0',
    label: 'truncate',
    badge:
      'rounded-full bg-(--btn-on) text-(--btn) px-1.5 text-[10px] font-semibold leading-4 min-w-4 text-center',
  },
  variants: {
    // --btn = the colour, --btn-on = what sits on it when filled
    severity: {
      primary: {
        base: '[--btn:var(--color-primary-900)]        [--btn-on:var(--color-on-primary)]',
      },
      secondary: {
        base: '[--btn:var(--color-on-surface-muted)] [--btn-on:var(--color-surface)]',
      },
      contrast: {
        base: '[--btn:var(--color-on-surface)]      [--btn-on:var(--color-surface)]',
      },
      success: {
        base: '[--btn:var(--color-status-success)]  [--btn-on:var(--color-on-status)]',
      },
      danger: {
        base: '[--btn:var(--color-status-danger)]   [--btn-on:var(--color-on-status)]',
      },
      info: {
        base: '[--btn:var(--color-status-info)]     [--btn-on:var(--color-on-status)]',
      },
      help: {
        base: '[--btn:var(--color-status-help)]     [--btn-on:var(--color-on-status)]',
      },
      warn: {
        base: '[--btn:var(--color-status-warn)]     [--btn-on:var(--color-on-status)]',
      },
      accent: {
        base: '[--btn:var(--color-on-surface-accent)] [--btn-on:var(--color-white)]',
      },
    },
    // severity: {
    //   neutral: {
    //     base: '[--button:var(--color-on-surface)] [--button-on:var(--color-brand-strong)]',
    //   },

    // },
    size: {
      xs: { base: 'h-7 px-2.5 text-xs', icon: 'size-3.5' },
      sm: { base: 'h-8 px-3 text-sm', icon: 'size-4' },
      md: { base: 'h-10 px-4 text-sm', icon: 'size-5' },
      lg: { base: 'h-12 px-5 text-base', icon: 'size-5' },
      vertical: { base: 'text-base py-2', icon: 'size-5' }
    },
    variant: {
      filled: {
        base: 'border-transparent bg-(--btn) text-(--btn-on) hover:bg-(--btn)/90 active:bg-(--btn)/80',
      },
      outlined: {
        base: 'border-(--btn)/50 bg-transparent text-(--btn) hover:bg-(--btn)/10 active:bg-(--btn)/15',
        badge: 'bg-(--btn) text-(--btn-on)',
      },
      text: {
        base: 'border-transparent bg-transparent text-(--btn) hover:bg-(--btn)/10 active:bg-(--btn)/15',
        badge: 'bg-(--btn) text-(--btn-on)',
      },
    },
    // variant: {
    //   filled: { base: 'border-transparent bg-(--tag) text-(--tag-on)' },
    //   outlined: { base: 'border-(--tag)/50 bg-transparent text-(--tag)' },
    //   text: {
    //     base: 'bg-transparent border-transparent text-(--button) hover:bg-(--button-on)/20',
    //   },
    // },
    iconOnly: { true: { base: 'px-0' } },
    pressed: { true: { base: 'bg-(--btn)/15' } },
    block: { true: { base: 'w-full' } },
  },
  compoundVariants: [
    // square icon-only buttons
    { iconOnly: true, size: 'xs', class: { base: 'w-7' } },
    { iconOnly: true, size: 'sm', class: { base: 'w-8' } },
    { iconOnly: true, size: 'md', class: { base: 'w-10' } },
    { iconOnly: true, size: 'lg', class: { base: 'w-12' } },
    // pressed on filled should darken, not tint
    { pressed: true, variant: 'filled', class: { base: 'bg-(--btn)/80' } },
  ],
  defaultVariants: {
    severity: 'primary',
    size: 'md',
    variant: 'filled',
  },
})
type ButtonVariants = VariantProps<typeof button>

const {
  label = undefined,
  icon = undefined,
  iconPosition = 'start',
  severity = undefined,
  size = undefined,
  variant = undefined,
  badge = undefined,
  loading = false,
  disabled = false,
  pressed = undefined,
  block = false,
  type = 'button',
  className = undefined,
} = defineProps<{
  label?: string
  icon?: LucideIcon
  iconPosition?: 'start' | 'end'
  severity?: ButtonVariants['severity']
  size?: ButtonVariants['size']
  variant?: ButtonVariants['variant']
  badge?: number | string
  loading?: boolean
  disabled?: boolean
  pressed?: boolean
  block?: boolean
  type?: 'button' | 'submit' | 'reset'
  className?: string
}>()

const slots = useSlots()
const hasContent = computed(() => !!label || !!slots.default)
const iconOnly = computed(() => !hasContent.value && !!icon)

const styles = computed(() =>
  button({ severity, size, variant, iconOnly: iconOnly.value, pressed, block }),
)

const iconComponent = computed(() => (loading ? Loader2 : icon))
const showIconAt = (pos: 'start' | 'end') =>
  !!iconComponent.value && (icon ? iconPosition === pos : pos === 'start')
</script>

<template>
  <Button
    :type="type"
    :class="styles.base({ class: className })"
    :disabled="disabled || loading"
    :aria-busy="loading || undefined"
    :aria-pressed="pressed === undefined ? undefined : pressed"
  >
    <Component
      :is="iconComponent"
      v-if="showIconAt('start')"
      :class="[styles.icon(), loading && 'animate-spin']"
      aria-hidden="true"
    />

    <slot>
      <span v-if="label" :class="styles.label()">{{ label }}</span>
    </slot>

    <Component
      :is="iconComponent"
      v-if="showIconAt('end')"
      :class="[styles.icon(), loading && 'animate-spin']"
      aria-hidden="true"
    />

    <span
      v-if="badge !== undefined && badge !== ''"
      :class="[styles.badge(), iconOnly && 'absolute -top-1.5 -right-1.5']"
    >
      {{ badge }}
    </span>
  </Button>
</template>
